import {Component, OnDestroy, OnInit} from '@angular/core';
import {AccountService} from '../account.service';
import {AccountPageModel} from '../models/account-page.model';
import {fadeInOnEnterAnimation, headShakeAnimation} from 'angular-animations';
import {AlertController} from '@ionic/angular';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {AuthService} from '../../../services/auth.service';
import {EventsService} from '../../../services/events.service';
import {Subscription} from 'rxjs';
import {AnalyticsService} from '../../../services/analytics.service';
import {UserService} from '../../../services/user.service';
import {TranslateService} from '@ngx-translate/core';
import {NavService} from '../../../services/nav.service';
import {environment} from '../../../../environments/environment';

@Component({
    selector: 'app-account-form',
    templateUrl: './account-form.component.html',
    styleUrls: ['./account-form.component.scss'],
    animations: [
        fadeInOnEnterAnimation({anchor: 'enter', duration: 300}),
        headShakeAnimation()]
})
export class AccountFormComponent implements OnInit, OnDestroy {

    subscriptions: Subscription[] = [];

    profileModel: AccountPageModel = null;
    profileForm: FormGroup = null;
    theme = 'default';

    public animationState = false;

    constructor(
        private event: EventsService,
        private accountService: AccountService,
        private authService: AuthService,
        public userService: UserService,
        private analytics: AnalyticsService,
        private alertController: AlertController,
        private translate: TranslateService,
        private nav: NavService,
        private fb: FormBuilder,
    ) {
    }

    static equalTo(group: FormGroup): { [err: string]: any | null } {
        const password1 = group.controls.password1;
        const password2 = group.controls.password2;
        const result: any = {};
        if ((password1.touched || password2.touched)) {
            if (password1.value !== password2.value) {
                password2.setErrors({equalTo: true});
                result.equalTo = true;
            } else {
                password2.setErrors(null);
            }
        }
        return result;
    }

    ngOnInit() {
        this.theme = environment.theme;
        this.userService.getCurrentUserInfo();
        this.accountService.getAccountPageData()
            .then(data => {
                this.profileModel = data;
                this.initForm();
            });
    }

    ionViewDidEnter() {
        this.analytics.trackView('Account Details');
    }

    onChange(data?: any) {
        this.event.publish('form:changed', 'account');
    }

    initForm() {
        this.profileForm = this.fb.group({
            first_name: [this.profileModel.first_name.value, [Validators.required]],
            last_name: [this.profileModel.last_name.value, [Validators.required]],
            password1: [this.profileModel.password1.value, [
                Validators.minLength(8),
                Validators.pattern('^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])' +
                    '(?=.*[{}\\[\\],.<>;:\'"?/|\\`~!@#$%^&*()_\\-+=])' +
                    '[a-zA-Z0-9{}\\[\\],.<>;:\'"?/|\\`~!@#$%^&*()_\\-+=]+$'),
            ]],
            password2: [this.profileModel.password2.value],
            email: [this.profileModel.email.value, Validators.required],
            phone: [this.profileModel.phone.value],
            lang_id: [this.profileModel.lang_id.value, [Validators.required]]
        }, {validator: AccountFormComponent.equalTo});

        this.subscriptions.push(this.profileForm.valueChanges.subscribe(data => this.onChange(data)));
    }

    onSubmit() {
        const controls = this.profileForm.controls;

        Object.keys(controls).forEach(key => {
            controls[key].markAsTouched();
        });

        if (this.profileForm.status !== 'VALID') {
            this.animateInvalidFields();
            this.alertController.create({
                header: this.translate.instant('__ALERT_ERROR_TITLE__'),
                message: this.translate.instant('__ALERT_ERROR_MESSAGE_REQUIRED_FIELDS__'),
                buttons: [this.translate.instant('__ALERT_ERROR_BUTTON__')]
            }).then(a => a.present());
        } else {
            this.profileModel.first_name.value = this.profileForm.value.first_name;
            this.profileModel.last_name.value = this.profileForm.value.last_name;
            this.profileModel.password1.value = this.profileForm.value.password1;
            this.profileModel.password2.value = this.profileForm.value.password2;
            this.profileModel.email.value = this.profileForm.value.email;
            this.profileModel.phone.value = this.profileForm.value.phone;
            this.profileModel.lang_id.value = this.profileForm.value.lang_id;

            this.accountService.saveMyProfileForm(this.profileModel)
                .then(data => {
                    if (data.hasOwnProperty('status')) {
                        this.event.publish('form:saved', {name: 'account', id: 'delId_' + this.userService.userInfo.id.toString()});

                        this.analytics.trackEvent('account_update', {});

                        this.alertController.create({
                            header: this.translate.instant('__ALERT_SUCCESS_TITLE__'),
                            message: this.translate.instant(data.status.message),
                            buttons: [{
                                text: this.translate.instant('__ALERT_SUCCESS_BUTTON__'),
                                handler: () => {
                                    this.userService.getUserInfo().then(() => {
                                      this.nav.navigateBack(['home']);
                                    });
                                }
                            }]
                        }).then(a => a.present());
                    } else if (data.hasOwnProperty('form')) {
                        this.profileModel = data.form;
                        if (this.profileModel.password1.error !== null) {
                            this.profileForm.controls.password1.setErrors({
                                custom: true
                            });
                        }
                        this.analytics.trackEvent('exception', {
                            description: 'Account Details Save Error: ' + this.profileModel.error,
                            fatal: false
                        });
                        this.alertController.create({
                            header: this.translate.instant('__ALERT_ERROR_TITLE__'),
                            message: this.translate.instant(this.profileModel.error),
                            buttons: [this.translate.instant('__ALERT_ERROR_BUTTON__')]
                        }).then(a => a.present());
                    }
                });
        }
    }

    animateInvalidFields() {
        this.animationState = false;
        setTimeout(() => {
            this.animationState = true;
        }, 1);
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(i => i.unsubscribe());
    }
}
